import { apiClient } from '@/api/client'
import {
  CANDIDATES,
  candidateByIdPath,
  candidateDocumentByIdPath,
  candidateDocumentsPath,
  candidateHirePath,
  candidateRejectPath,
} from '@/api/endpoints'
import type { ApiResponseWrapper } from '@/types/api.types'
import type { DocumentCategory, UserDocument } from './documents.service'

export type CandidateStatus = 'interviewing' | 'trainee' | 'offered' | 'hired' | 'rejected'
export type ProbationPeriod = '1-month' | '3-months' | 'custom'

export interface Candidate {
  _id: string
  organizationId: string
  companyId?: string
  firstName: string
  lastName: string
  email: string
  phone?: string
  fatherName?: string
  motherName?: string
  cnicNumber?: string
  dateOfBirth?: string
  qualification?: string
  maritalStatus?: string
  address?: string
  emergencyContactNumber?: string
  bankName?: string
  accountTitle?: string
  branch?: string
  branchCode?: string
  accountNumber?: string
  appliedPosition?: string
  source?: string
  expectedSalary?: number
  interviewDate?: string
  notes?: string
  status: CandidateStatus
  hiredUserId?: string
  decision?: {
    decidedAt?: string
    decidedByUserId?: string
    reason?: string
  }
  createdAt?: string
  updatedAt?: string
}

export interface CandidatePayload {
  firstName: string
  lastName: string
  email: string
  phone?: string
  fatherName?: string
  motherName?: string
  cnicNumber?: string
  dateOfBirth?: string
  qualification?: string
  maritalStatus?: string
  address?: string
  emergencyContactNumber?: string
  bankName?: string
  accountTitle?: string
  branch?: string
  branchCode?: string
  accountNumber?: string
  companyId?: string
  appliedPosition?: string
  source?: string
  expectedSalary?: number
  interviewDate?: string
  notes?: string
  status?: CandidateStatus
}

export interface HireCandidatePayload {
  roleId: string
  companyId: string
  joiningDate: string
  workLocation?: 'remote' | 'onsite' | 'hybrid'
  department?: string
  designation?: string
  compensationType: 'salaried' | 'hourly'
  baseSalary?: number
  hourlyRate?: number
  currency?: string
  bankName?: string
  accountTitle?: string
  branch?: string
  branchCode?: string
  accountNumber?: string
  employmentStatus?: 'probation' | 'permanent'
  probationPeriod?: ProbationPeriod
  probationEndDate?: string
}

export interface CandidateListParams {
  status?: CandidateStatus
  companyId?: string
  search?: string
}

interface DocumentListPayload {
  message?: string
  data: UserDocument[]
}

interface UploadResponsePayload {
  message?: string
  data: {
    category: string
    documentGroupId: string
    files: Array<Omit<UserDocument, 'category'>>
  }
}

function unwrapDocuments(res: unknown): UserDocument[] {
  const wrapped = res as ApiResponseWrapper<DocumentListPayload>
  return Array.isArray(wrapped?.data?.data) ? wrapped.data.data : []
}

function unwrapUpload(res: unknown): UserDocument[] {
  const wrapped = res as ApiResponseWrapper<UploadResponsePayload>
  const inner = wrapped?.data
  if (!inner?.data?.files) return []
  const { category, documentGroupId, files } = inner.data
  return files.map((file) => ({ ...file, category, documentGroupId: documentGroupId ?? null }))
}

export async function listCandidates(params?: CandidateListParams): Promise<Candidate[]> {
  const { data } = await apiClient.get<ApiResponseWrapper<Candidate[]>>(CANDIDATES, { params })
  return Array.isArray(data?.data) ? data.data : []
}

export async function createCandidate(payload: CandidatePayload): Promise<Candidate> {
  const { data } = await apiClient.post<ApiResponseWrapper<Candidate>>(CANDIDATES, payload)
  return data.data
}

export async function updateCandidate(
  id: string,
  payload: Partial<CandidatePayload>,
): Promise<Candidate> {
  const { data } = await apiClient.patch<ApiResponseWrapper<Candidate>>(
    candidateByIdPath(id),
    payload,
  )
  return data.data
}

export async function rejectCandidate(id: string, reason?: string): Promise<Candidate> {
  const { data } = await apiClient.post<ApiResponseWrapper<Candidate>>(candidateRejectPath(id), {
    reason,
  })
  return data.data
}

export async function hireCandidate(
  id: string,
  payload: HireCandidatePayload,
): Promise<{ candidate: Candidate; user: unknown }> {
  const { data } = await apiClient.post<ApiResponseWrapper<{ candidate: Candidate; user: unknown }>>(
    candidateHirePath(id),
    payload,
  )
  return data.data
}

export async function listCandidateDocuments(
  id: string,
  category?: DocumentCategory,
): Promise<UserDocument[]> {
  const { data } = await apiClient.get<ApiResponseWrapper<DocumentListPayload>>(
    candidateDocumentsPath(id),
    { params: { ...(category ? { category } : {}) } },
  )
  return unwrapDocuments(data)
}

export async function uploadCandidateDocuments(
  id: string,
  files: File[],
  category: Extract<DocumentCategory, 'interview-form' | 'candidate-documents'>,
  documentType?: string,
): Promise<UserDocument[]> {
  const formData = new FormData()
  for (const file of files) formData.append('files', file)
  const { data } = await apiClient.post<ApiResponseWrapper<UploadResponsePayload>>(
    candidateDocumentsPath(id),
    formData,
    { params: { category, ...(documentType ? { documentType } : {}) } },
  )
  return unwrapUpload(data)
}

export async function deleteCandidateDocument(
  id: string,
  documentId: string,
): Promise<void> {
  await apiClient.delete(candidateDocumentByIdPath(id, documentId))
}
